import React from "react";
import { useTranslation } from 'react-i18next';
import '../assets/styles/Project.scss';

// Placeholder component for project images
const ProjectPlaceholder = ({ title, isVideo = false, videoId = "", gifSrc = "" }: { title: string; isVideo?: boolean; videoId?: string; gifSrc?: string }) => {
    if (isVideo && videoId) {
        return (
            <div className="project-video-preview">
                <img 
                    src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                    alt={`${title} video preview`}
                    className="video-thumbnail"
                    onError={(e) => {
                        // Fallback to maxresdefault if hqdefault fails
                        const target = e.target as HTMLImageElement;
                        if (target.src.includes('hqdefault.jpg')) {
                            target.src = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
                        }
                    }}
                />
                <div className="play-button-overlay">
                    <div className="play-icon">▶</div>
                </div>
            </div>
        );
    }
    
    if (gifSrc) {
        return (
            <div className="project-gif-preview">
                <img 
                    src={gifSrc}
                    alt={`${title} preview`}
                    className="gif-thumbnail"
                />
            </div>
        );
    }
    
    return (
        <div className="project-placeholder">
            <div className="placeholder-content">
                <div className="placeholder-icon">📱</div>
                <div className="placeholder-text">{title}</div>
            </div>
        </div>
    );
};

function Project() {
    const { t } = useTranslation();
    
    return(
    <div className="projects-container" id="projects">
        <h1>{t('projects.title')}</h1>
        <div className="projects-grid">
            <article className="project project--speclens">
                <a href="https://speclens.tinamk.no/" target="_blank" rel="noreferrer">
                    <h2>{t('projects.speclens.title')}</h2>
                </a>
                <p>{t('projects.speclens.description')}</p>
                <div className="speclens-media project-media">
                    <a
                        className="speclens-image-link"
                        href="https://speclens.tinamk.no/"
                        target="_blank"
                        rel="noreferrer"
                        aria-label={t('projects.speclens.openSite')}
                    >
                        <img
                            src={require('../assets/images/speclens-report.png')}
                            alt={t('projects.speclens.imageAlt')}
                            loading="lazy"
                        />
                    </a>
                    <div className="speclens-media-actions">
                        <a href="https://speclens.tinamk.no/" target="_blank" rel="noreferrer">
                            {t('projects.speclens.openSite')}
                        </a>
                        <a href={`${process.env.PUBLIC_URL}/Bacheloroppgave_Tina_og_Kristine.pdf`} target="_blank" rel="noreferrer">
                            {t('projects.speclens.readThesis')}
                        </a>
                    </div>
                </div>
            </article>
            <article className="project project--dagly">
                <a href="https://www.dagly.no/" target="_blank" rel="noreferrer">
                    <h2>{t('projects.dagly.title')}</h2>
                </a>
                <p>{t('projects.dagly.description')}</p>
                <a
                    className="dagly-media project-media"
                    href="https://www.dagly.no/demo/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t('projects.dagly.tryDemo')}
                >
                    <p className="dagly-media-label">{t('projects.dagly.viewsTitle')}</p>
                    <div className="dagly-showcase" aria-label={t('projects.dagly.galleryLabel')}>
                        <figure className="dagly-screen">
                            <img src={require('../assets/images/dagly-bilde.png')} alt={t('projects.dagly.imageViewAlt')} loading="lazy" />
                            <figcaption>{t('projects.dagly.imageView')}</figcaption>
                        </figure>
                        <figure className="dagly-screen">
                            <img src={require('../assets/images/dagly-enkel.png')} alt={t('projects.dagly.simpleViewAlt')} loading="lazy" />
                            <figcaption>{t('projects.dagly.simpleView')}</figcaption>
                        </figure>
                        <figure className="dagly-screen">
                            <img src={require('../assets/images/dagly-full.png')} alt={t('projects.dagly.fullViewAlt')} loading="lazy" />
                            <figcaption>{t('projects.dagly.fullView')}</figcaption>
                        </figure>
                    </div>
                </a>
            </article>
            <div className="project">
                <h2>{t('projects.poengTavla.title')}</h2>
                <p>{t('projects.poengTavla.description')}</p>
                <div className="project-preview project-media"><ProjectPlaceholder title={t('projects.poengTavla.title')} gifSrc={require('../assets/images/PoengTavla.gif')} /></div>
            </div>
            <div className="project">
                <a href="https://youtu.be/k2xDk3BzuLM" target="_blank" rel="noreferrer"><h2>{t('projects.swiftCarRental.title')}</h2></a>
                <p>{t('projects.swiftCarRental.description')}</p>
                <a className="project-media" href="https://youtu.be/k2xDk3BzuLM" target="_blank" rel="noreferrer"><ProjectPlaceholder title={t('projects.swiftCarRental.title')} isVideo={true} videoId="k2xDk3BzuLM" /></a>
            </div>
            <div className="project">
                <a href="https://youtu.be/gtixmN50iF0" target="_blank" rel="noreferrer"><h2>{t('projects.wodApp.title')}</h2></a>
                <p>{t('projects.wodApp.description')}</p>
                <a className="project-media" href="https://youtu.be/gtixmN50iF0" target="_blank" rel="noreferrer"><ProjectPlaceholder title={t('projects.wodApp.title')} isVideo={true} videoId="gtixmN50iF0" /></a>
            </div>
            <div className="project">
                <a href="https://youtu.be/EQFfBlNMe3A" target="_blank" rel="noreferrer"><h2>{t('projects.smartwatch.title')}</h2></a>
                <p>{t('projects.smartwatch.description')}</p>
                <a className="project-media" href="https://youtu.be/EQFfBlNMe3A" target="_blank" rel="noreferrer"><ProjectPlaceholder title={t('projects.smartwatch.title')} isVideo={true} videoId="EQFfBlNMe3A" /></a>
            </div>
            <div className="project">
                <a href="https://youtu.be/xY8ED5OUsxU" target="_blank" rel="noreferrer"><h2>{t('projects.kritiotter.title')}</h2></a>
                <p>{t('projects.kritiotter.description')}</p>
                <a className="project-media" href="https://youtu.be/xY8ED5OUsxU" target="_blank" rel="noreferrer"><ProjectPlaceholder title={t('projects.kritiotter.title')} isVideo={true} videoId="xY8ED5OUsxU" /></a>
            </div>
            <div className="project">
                <a href="https://youtu.be/LZWvWO9-PfU" target="_blank" rel="noreferrer"><h2>{t('projects.pixelPet.title')}</h2></a>
                <p>{t('projects.pixelPet.description')}</p>
                <a className="project-media" href="https://youtu.be/LZWvWO9-PfU" target="_blank" rel="noreferrer"><ProjectPlaceholder title={t('projects.pixelPet.title')} isVideo={true} videoId="LZWvWO9-PfU" /></a>
            </div>
        </div>
    </div>
    );
}

export default Project;
